import './perks-post-comment.component.scss';

import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';

import { IconComments } from '../../../../../common/icons/icon-comments';
import { IconEllipsis } from '../../../../../common/icons/icon-ellipsis';
import { IconGhost } from '../../../../../common/icons/icon-ghost';
import { IconHeart } from '../../../../../common/icons/icon-heart';
import { IconHeartOutline } from '../../../../../common/icons/icon-heart-outline';
import { IconReply } from '../../../../../common/icons/icon-reply';
import { Pageable } from '../../../../../common/page/pageable.model';
import { useTranslation } from '../../../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { buttonize } from '../../../../../utils/a11y.utils';
import { CommentState } from '../../../../model/comment.interface';
import { ActionType } from '../../../../model/user-action.inteface';
import { PerksHelper } from '../../../../perks.helper';
import { replyTo } from '../../../../store/perks.reducer';
import {
  selectPostCommentsLoading,
  selectPostCommentsLoadRequestParentCommentId,
  selectPostCommentsReplyTo,
  selectPostCountsLoading,
  selectUser,
} from '../../../../store/perks.selectors';
import { archiveComment, loadPostComments, postUserAction } from '../../../../store/perks.thunk';
import { useDisplayIdentity } from '../../../../use-display-identity.hook';
import { PerksAvatar } from '../../../perks-avatar/perks-avatar.component';
import { focusMobileCommentInput } from '../comment-entry';
import { PerksPostCommentForm } from '../perks-post-comment-form/perks-post-comment-form.component';

export const PerksPostComment = ({ key, post, comment }) => {
  const { t } = useTranslation();
  const commenter = useDisplayIdentity(comment.user);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const countsLoading = useAppSelector(selectPostCountsLoading(post.id));
  const commentsLoading = useAppSelector(selectPostCommentsLoading(post.id));
  const loadParentCommentId = useAppSelector(selectPostCommentsLoadRequestParentCommentId);
  const commentReplyTo = useAppSelector(selectPostCommentsReplyTo);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  // derived, not mirrored: a local copy drifted from the store whenever the form was hidden by a
  // collapse, and the next Reply tap then closed a form the user could not see
  const isReplyOpen = commentReplyTo === comment.id;

  const isArchived = useMemo(() => {
    return comment.state === CommentState.ARCHIVED;
  }, [comment.state]);

  const getCommentCount = () => {
    const count = post.counts?.value?.commentCounts?.find((c) => c.commentId === comment.id);
    return count ? count.likes : 0;
  };

  const deleteComment = (event) => {
    stopEvent(event);
    if (!comment.id) return;
    dispatch(archiveComment({ postId: post.id, commentId: comment.id }));
  };

  const handleLikeComment = (event, like) => {
    stopEvent(event);
    const action = {
      postId: post.id,
      commentId: comment.id,
      actionType: like ? ActionType.LIKE : ActionType.REMOVE_LIKE,
    };
    dispatch(postUserAction(action));
  };

  const handleReplyPage = (event) => {
    stopEvent(event);
    const pageNumber = comment.children ? comment.children.number + 1 : 0;
    const pageSize = comment.children
      ? comment.children.size
      : PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE;

    dispatch(
      loadPostComments({
        postId: post.id,
        parentCommentId: comment.id,
        commentDepth: PerksHelper.COMMENTS_DEFAULT_DEPTH,
        childPageSize: PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE,
        pageable: Pageable.of(pageNumber, pageSize, PerksHelper.COMMENTS_DEFAULT_SORT),
      })
    );
  };

  const toggleCollapse = (event) => {
    stopEvent(event);
    // the whole reply-form container, padding included — a click beside the form is not "collapse"
    if (isElementInTree('single-comment-form-container', event.target)) {
      return;
    }
    const collapsing = !isCollapsed;
    if (collapsing && isReplyOpen) {
      // collapsing a comment ends the reply; expanding it later starts clean
      dispatch(replyTo(null));
    }
    setIsCollapsed(collapsing);
  };

  const toggleDropdown = (event) => {
    stopEvent(event);
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleReply = (event) => {
    stopEvent(event);
    const newReplyOpen = !isReplyOpen;
    dispatch(replyTo(newReplyOpen ? comment.id : null));
    if (newReplyOpen) {
      // Replying to a collapsed comment expands it. stopEvent above keeps this tap from reaching
      // toggleCollapse, and the form renders only while expanded, so without this the tap set a
      // reply target and nothing opened. Showing the reply count on a collapsed comment makes
      // that the obvious thing to try, which is why it surfaced now.
      setIsCollapsed(false);
      // Phones reply through the fixed bar, which is already mounted. The
      // desktop inline form below focuses itself when it mounts.
      focusMobileCommentInput();
    }
  };

  const isElementInTree = (className, element) => {
    if (!element) {
      return false;
    }
    if (element.classList?.contains(className)) {
      return true;
    }
    return isElementInTree(className, element.parentElement);
  };

  const stopEvent = (event) => {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const canModerate = user && comment.user && (user.moderator || user.id === comment.user.id);
  // Collapsing hides the replies, so their count moves up into the actions row to say what is
  // folded away. Only the loaded children page knows the total; a comment deeper than the fetched
  // depth has `hasChildren` but no count, and stating nothing beats guessing one.
  const replyCount = comment.children?.totalElements ?? 0;

  return (
    <div
      class={`single-comment ${isCollapsed ? 'single-collapsed' : ''} ${comment.shadowBanned ? 'single-shadow-banned' : ''}`}
      data-comment-id={comment.id}
      {...buttonize(toggleCollapse, t(isCollapsed ? 'a11y.expandComment' : 'a11y.collapseComment'))}
      aria-expanded={!isCollapsed}
    >
      {/* header + text only, so a highlight never spreads to the replies below */}
      <div class="single-comment-main">
        <div class="single-comment-header">
          {!isArchived && <PerksAvatar user={comment.user} size={50} />}
          <div
            class={`single-comment-header-author ${isArchived ? 'single-archived' : ''}`}
            attachToMobileFooter
          >
            <div class="single-comment-author-line">
              <h5 class="h5 single-h5">{isArchived ? t('perks.commentRemoved') : commenter.name}</h5>
              {/* only moderators and admins ever receive shadowBanned; the fan themself sees nothing */}
              {comment.shadowBanned && !isArchived && (
                <span
                  class="single-shadow-ban-marker"
                  role="img"
                  title={t('perks.shadowBanned')}
                  aria-label={t('perks.shadowBanned')}
                >
                  <IconGhost />
                </span>
              )}
            </div>
            <p class="single-comment-date">{formatDate(comment.createdAt)}</p>
          </div>

          {canModerate && !isArchived && !isCollapsed && (
            <div class="single-comment-dropdown-actions">
              <div class={`single-dropdown ${isDropdownOpen ? 'show' : ''}`}>
                <div
                  class="single-ellipsis-btn"
                  {...buttonize(toggleDropdown, t('a11y.commentMenu'))}
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  <IconEllipsis />
                </div>
                {isDropdownOpen && (
                  <div class="single-dropdown-menu">
                    <div class="single-btn-delete" {...buttonize(deleteComment)}>
                      {t('common.delete')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div class={`single-comment-text-actions-wrapper ${isCollapsed ? 'single-collapsed' : ''}`}>
          <div class={`single-comment-text-container ${isArchived ? 'single-archived' : ''}`}>
            {!isArchived ? (
              <p class="single-comment-text">{comment.textContent}</p>
            ) : (
              <p class="single-comment-text archived">{t('perks.commentHasBeenRemoved')}</p>
            )}
          </div>

          <div class="single-comment-actions">
            {!isArchived && (
              <div class="single-comment-action">
                {!comment.userLiked ? (
                  <IconHeartOutline {...buttonize((e) => handleLikeComment(e, true), t('a11y.likeComment'))} />
                ) : (
                  <IconHeart {...buttonize((e) => handleLikeComment(e, false), t('a11y.unlikeComment'))} />
                )}
                <span class="single-comment-count">{countsLoading ? '-' : getCommentCount()}</span>
              </div>
            )}
            {!isArchived && isCollapsed && replyCount > 0 && (
              <div class="single-comment-action single-comment-replies">
                <IconComments width={20} height={16} />
                <span class="single-comment-count">
                  {t(replyCount === 1 ? 'perks.replyCount.one' : 'perks.replyCount.other', {
                    count: `${replyCount}`,
                  })}
                </span>
              </div>
            )}
            {!isArchived && (
              <div
                {...buttonize(toggleReply, t('a11y.reply'))}
                class={`single-comment-action ${commentReplyTo === comment.id ? 'single-comment-action-active' : ''}`}
              >
                <IconReply />
                <span class="single-comment-count single-mobile-hidden">{t('perks.reply')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {commentReplyTo === comment.id && !isCollapsed && (
        <div class="single-comment-form-container" attachToMobileFooter>
          <PerksPostCommentForm
            post={post}
            parentComment={comment}
            placeholder={t('perks.replyToComment')}
            focusOnMount={true}
          />
        </div>
      )}

      {comment.hasChildren && !isCollapsed && (
        <div class="single-comment-child-container">
          {comment.children?.content?.length > 0 && (
            <>
              {comment.children.content.map((child) => (
                <PerksPostComment key={child.id} post={post} comment={child} />
              ))}
            </>
          )}

          {!comment.children?.last && (
            <section class="single-load-more">
              <button class="button btn" disabled={commentsLoading} onClick={handleReplyPage}>
                {(!commentsLoading || loadParentCommentId !== comment.id) && !comment.children && (
                  <span>{t('perks.viewReplies')}</span>
                )}
                {(!commentsLoading || loadParentCommentId !== comment.id) && comment.children && (
                  <span>{t('perks.loadMoreReplies')}</span>
                )}
                {commentsLoading && loadParentCommentId === comment.id && (
                  <span>{t('common.loading')}</span>
                )}
              </button>
            </section>
          )}
        </div>
      )}
    </div>
  );
};
